/**
 * Remembers the public collection a not-yet-registered visitor was reading when
 * a sign-up wall sent them into the auth flow, so they can be offered a link
 * back once they land in their account.
 *
 * Every wall on a public collection (the comment and reply CTA, the like button
 * on a comment, the "get rubra" button, the logo, the mobile signup banner)
 * ends up navigating to the same handful of auth paths, so the decision is made
 * once from the navigation itself rather than at each of those call sites.
 *
 * The profile overview `/u/:username/collections` is deliberately not covered:
 * it names no collection, so there would be nothing to put in the offer.
 */

import Collection from '@/store/models/collections/Collection'
import { isAuthFlowPath } from '@/utils/auth-redirect'

// `/u/:username/collections/:id` and anything below it. Anchored, so it can
// never match a protocol-relative path.
const PUBLIC_COLLECTION_PATH_RE = /^\/u\/[^/]+\/collections\/[^/]+/

export const PublicCollectionReturnAction = {
  CAPTURE: 'capture',
  CLEAR: 'clear',
  KEEP: 'keep',
}

/**
 * @param {String} [path] - a route path
 * @returns {Boolean} whether it points inside a public collection
 */
export const isPublicCollectionPath = path =>
  typeof path === 'string' && PUBLIC_COLLECTION_PATH_RE.test(path)

// the auth flow entry points are defined once, next to the authRedirect rules
export { isAuthFlowPath }

/**
 * What a navigation should do with the stored return target.
 *
 * A guest account has a userId but still goes through signup to become a full
 * one, so the capture is gated on `hasFullAccount` rather than on being logged
 * out — otherwise guests upgrading from the comment wall would lose their way
 * back.
 *
 * @param {Object} params
 * @param {String} params.toPath - path being navigated to
 * @param {String} params.fromPath - path being navigated away from
 * @param {Boolean} params.hasFullAccount - auth/hasFullAccount
 * @returns {String} a PublicCollectionReturnAction value
 */
export function publicCollectionReturnAction({ toPath, fromPath, hasFullAccount }) {
  // back inside the collection: the offer has served its purpose, or the
  // existing authRedirect already returned the user without it
  if (isPublicCollectionPath(toPath)) return PublicCollectionReturnAction.CLEAR

  if (!hasFullAccount && isAuthFlowPath(toPath) && isPublicCollectionPath(fromPath)) {
    return PublicCollectionReturnAction.CAPTURE
  }

  return PublicCollectionReturnAction.KEEP
}

/**
 * @param {Object} params
 * @param {String} params.fullPath - the exact public page the visitor left,
 *   down to the item detail, so they return where they wanted to comment
 * @param {String} params.ownerUsername - the `:username` segment, which is also
 *   the creator's display name
 * @param {String} [params.collectionName] - null when the collection entity was
 *   not loaded yet; the notice has a text variant for that
 * @returns {Object} the stored return target
 */
export function buildPublicCollectionReturn({ fullPath, ownerUsername, collectionName }) {
  return {
    path: fullPath,
    ownerUsername,
    collectionName: collectionName ?? null,
  }
}

/**
 * Applies the decision above to a navigation. Called from the global router
 * guard, which is the one place all the sign-up walls funnel through.
 *
 * @param {Object} store - the vuex store
 * @param {Object} to - target route
 * @param {Object} from - route being left
 */
export function trackPublicCollectionReturn(store, to, from) {
  const action = publicCollectionReturnAction({
    toPath: to.path,
    fromPath: from.path,
    hasFullAccount: store.getters['auth/hasFullAccount'],
  })

  if (action === PublicCollectionReturnAction.KEEP) return

  if (action === PublicCollectionReturnAction.CLEAR) {
    if (store.getters['auth/publicCollectionReturn']) {
      store.commit('auth/setPublicCollectionReturn', null)
    }
    return
  }

  store.commit('auth/setPublicCollectionReturn', buildPublicCollectionReturn({
    fullPath: from.fullPath,
    ownerUsername: from.params.username,
    // the public views insert the collection on load, so the name is normally
    // there; the notice has a text variant for when it is not
    collectionName: Collection.find(from.params.id)?.name,
  }))
}
